/**
 * The report-code picker for the manual reporting-period dialog (#220).
 *
 * A new treasurer won't know which report codes exist or what they mean, so a bare
 * free-text box is poor discoverability. This shows the jurisdiction's recognized report
 * types as "CODE — plain-English name" options, filterable as you type — but it is
 * deliberately CREATABLE: a code that isn't on the list can still be entered ("Use …"),
 * because the recognized set is state-published and evolves and we never want to trap a
 * legitimate filing (the same warn-don't-block stance the backend takes). Built on the same
 * Popover + cmdk primitives as {@link ContactCombobox}; used only where the jurisdiction
 * publishes a code list (LA) — elsewhere the dialog renders a plain input.
 */
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { RecognizedReportTypeDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface ReportTypeComboboxProps {
  /** Ties the trigger to an external `<label htmlFor>`. */
  id?: string;
  /** The current report code — may be a free-text value that isn't in `options`. */
  value: string;
  onChange: (code: string) => void;
  options: RecognizedReportTypeDto[];
  placeholder?: string;
  testId?: string;
}

export function ReportTypeCombobox({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select or type a report code…',
  testId,
}: ReportTypeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const trimmed = value.trim();
  const selected = options.find((o) => o.code.toUpperCase() === trimmed.toUpperCase());

  const q = query.trim();
  const qUpper = q.toUpperCase();
  const filtered = q
    ? options.filter(
        (o) => o.code.toUpperCase().includes(qUpper) || o.name.toUpperCase().includes(qUpper),
      )
    : options;
  // Offer a free-text "use what I typed" item when the query isn't an exact code we list —
  // preserves the escape hatch for a legitimate code we haven't catalogued yet.
  const isExactCode = options.some((o) => o.code.toUpperCase() === qUpper);
  const showCreate = q.length > 0 && !isExactCode;

  const commit = (code: string) => {
    onChange(code);
    setQuery('');
    setOpen(false);
  };

  const triggerLabel = selected
    ? `${selected.code} — ${selected.name}`
    : trimmed || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
            !trimmed && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Own the filtering (shouldFilter=false) so the creatable item and case-insensitive
            code/name search behave predictably rather than via cmdk's default matcher. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type a code…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {filtered.length === 0 && !showCreate && (
              <CommandEmpty>No matching report codes.</CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o.code} value={o.code} onSelect={() => commit(o.code)}>
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected?.code === o.code ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">
                      <span className="font-medium">{o.code}</span>
                      <span className="text-muted-foreground"> — {o.name}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__use__${q}`}
                  onSelect={() => commit(q)}
                  data-testid="report-type-use-custom"
                >
                  Use “{q}”
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
